import { FC, useMemo, useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { useGuardianState } from '../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useGuardianDocumentsHook,
  useGuardianDownloadDocumentHook,
} from '../../hooks/useGuardianDocumentsHook';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { GuardianDocument } from '../../../../../apis/guardian/sub-apis/useGuardianDocumentsService';
import { Download, PictureAsPdf, Image, Description, InsertDriveFile, Search, Clear } from '@mui/icons-material';

const GuardianDocumentsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isLoading, error } = useGuardianDocumentsHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
      ...(categoryFilter !== 'all' && { category: categoryFilter }),
      ...(typeFilter !== 'all' && { type: typeFilter }),
    }
  );

  const downloadMutation = useGuardianDownloadDocumentHook();

  const documents = useMemo(() => data?.data || [], [data]);
  // const totalCount = data?.count || 0;

  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter(
      (doc) =>
        doc.title.toLowerCase().includes(query) ||
        doc.category.toLowerCase().includes(query) ||
        doc.type.toLowerCase().includes(query)
    );
  }, [documents, searchQuery]);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(documents.map((d) => d.category)));
    return uniqueCategories.sort();
  }, [documents]);

  const fileTypes = useMemo(() => {
    const uniqueTypes = Array.from(new Set(documents.map((d) => d.type)));
    return uniqueTypes.sort();
  }, [documents]);

  const getFileIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <PictureAsPdf sx={{ color: 'error.main' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image sx={{ color: 'primary.main' }} />;
      case 'doc':
      case 'docx':
        return <Description sx={{ color: 'info.main' }} />;
      default:
        return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownload = useCallback((doc: GuardianDocument) => {
    if (!selectedChildId) return;

    downloadMutation.mutate(
      {
        studentId: selectedChildId,
        documentId: doc.id,
      },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = doc.title || `document-${doc.id}.${doc.type}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
      }
    );
  }, [selectedChildId, downloadMutation]);

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'type',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return getFileIcon(params.value as string);
      },
    },
    {
      field: 'title',
      headerName: translateText('guardian.documents.columns.title'),
      width: 250,
      flex: 1,
    },
    {
      field: 'category',
      headerName: translateText('guardian.documents.columns.category'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            color="primary"
          />
        );
      },
    },
    {
      field: 'type',
      headerName: translateText('guardian.documents.columns.type'),
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        return params.value.toUpperCase();
      },
    },
    {
      field: 'fileSize',
      headerName: translateText('guardian.documents.columns.fileSize'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        return formatFileSize(params.value as number);
      },
    },
    {
      field: 'uploadDate',
      headerName: translateText('guardian.documents.columns.uploadDate'),
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      field: 'actions',
      headerName: translateText('common.actions'),
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const document = params.row as GuardianDocument;
        return (
          <Tooltip title={translateText('guardian.documents.download')}>
            <IconButton
              size="small"
              onClick={() => handleDownload(document)}
              disabled={downloadMutation.isPending}
              data-testid={`download-doc-btn-${document.id}`}
            >
              <Download />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ], [translateText, downloadMutation.isPending, handleDownload]);

  const rows = useMemo(() => {
    return filteredDocuments.map((doc) => ({
      id: doc.id,
      title: doc.title,
      category: doc.category,
      type: doc.type,
      fileSize: doc.fileSize,
      uploadDate: doc.uploadDate,
      fileUrl: doc.fileUrl,
    }));
  }, [filteredDocuments]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-documents-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-documents-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-documents-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-documents-container">
      <Grid item xs={12}>
        <Typography variant="h5">
          {translateText('guardian.documents.title')}
        </Typography>
        {selectedChildId && (
          <Typography variant="body2" color="text.secondary">
            {translateText('guardian.documents.selectedChild')}: {selectedChildId}
          </Typography>
        )}
      </Grid>

      {/* Search and Filters */}
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="guardian-documents-filters"
        >
          <TextField
            fullWidth
            placeholder={translateText('guardian.documents.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    data-testid="guardian-documents-search-clear"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
            data-testid="guardian-documents-search"
          />
          {categories.length > 0 && (
            <TextField
              select
              label={translateText('guardian.documents.filters.category')}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ minWidth: 150 }}
              size="small"
              data-testid="guardian-documents-category-filter"
            >
              <MenuItem value="all">{translateText('common.all')}</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          )}
          {fileTypes.length > 0 && (
            <TextField
              select
              label={translateText('guardian.documents.filters.type')}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              sx={{ minWidth: 150 }}
              size="small"
              data-testid="guardian-documents-type-filter"
            >
              <MenuItem value="all">{translateText('common.all')}</MenuItem>
              {fileTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </Grid>

      {/* Documents Grid */}
      <Grid item xs={12}>
        {filteredDocuments.length === 0 && !isLoading ? (
          <Alert severity="info">
            {searchQuery
              ? translateText('guardian.documents.noSearchResults')
              : translateText('guardian.documents.noDocuments')}
          </Alert>
        ) : (
          <PaginatedGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            dataTestId="guardian-documents-grid"
            serverSide={false}
            totalCount={filteredDocuments.length}
            currentPage={page}
            currentPageSize={pageSize}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default GuardianDocumentsView;
